import React, { useState, useEffect } from 'react';
import { Star, DollarSign } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import DonationModal from '../components/donation/DonationModal';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // TODO: Implement feedback submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSubmitting(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-[#272727] rounded-xl p-6 max-w-md w-full">
        <h3 className="text-xl font-semibold text-white mb-4">Provide Feedback</h3>
        
        <form onSubmit={handleSubmit}>
          <div className="flex justify-center space-x-2 mb-6">
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setRating(value)}
                className="focus:outline-none"
              >
                <Star
                  className={`h-8 w-8 ${
                    value <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'
                  }`}
                />
              </button>
            ))}
          </div>

          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Tell us about your experience..."
            className="w-full px-4 py-3 bg-[#272727] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-[#404040] hover:border-[#505050] transition-colors min-h-[120px] resize-none mb-4"
            required
          />

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || rating === 0}
              className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:bg-purple-400/50 disabled:cursor-not-allowed flex items-center"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
                  Submitting...
                </>
              ) : (
                'Submit Feedback'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const About: React.FC = () => {
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [showDonationSuccess, setShowDonationSuccess] = useState(false);

  useEffect(() => {
    const donationStatus = searchParams.get('donation');
    if (donationStatus === 'success') {
      setShowDonationSuccess(true);
      // Clear the URL parameter
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams]);

  return (
    <div className="max-w-4xl mx-auto">
      {showDonationSuccess && (
        <div className="mb-6 bg-green-900/50 border border-green-700 text-green-200 px-6 py-4 rounded-lg flex items-center justify-between">
          <p>Thank you for your donation! Your support helps us maintain and improve Spectacle.</p>
          <button 
            onClick={() => setShowDonationSuccess(false)}
            className="text-green-200 hover:text-white ml-4"
          >
            ×
          </button>
        </div>
      )}

      <div className="space-y-12">
        {/* Company Bio */}
        <section className="bg-[#272727] rounded-xl p-8">
          <h2 className="text-2xl font-bold text-white mb-4">Our Mission</h2>
          <p className="text-gray-300 leading-relaxed">
            Spectacle is a movement, intended to change the status quo of how news is created and shared. 
            News is based on information and information is based on facts. Facts are hard to find in a 
            world of opinion and misinformation. Help us build a strong community of fact seekers and be 
            part of making the world a better place with information synergy. We are all in this together.
          </p>
        </section>

        {/* Founder Section */}
        <section className="bg-[#272727] rounded-xl p-8">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
            <div className="order-2 md:order-1">
              <h2 className="text-2xl font-bold text-white mb-4">Meet Our Founder</h2>
              <p className="text-gray-300 leading-relaxed">
                Colby Harper, our founder, had a vision to build a platform that can stop the growing 
                divide that misinformation causes in our society and bring us all together by focusing 
                on what truly matters. Facts, information, and community.
              </p>
            </div>
            <img
              src="/photo 7.jpeg"
              alt="Colby Harper"
              className="w-48 h-48 object-cover object-top rounded-lg order-1 md:order-2"
            />
          </div>
        </section>

        {/* Feedback Section */}
        <section className="bg-[#272727] rounded-xl p-8">
          <h2 className="text-2xl font-bold text-white mb-4">Your Feedback Matters</h2>
          <p className="text-gray-300 mb-6">
            We're constantly working to improve Spectacle and your feedback helps us create a better 
            platform for everyone. Share your thoughts and suggestions with us.
          </p>
          <button
            onClick={() => setShowFeedbackModal(true)}
            className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors flex items-center"
          >
            <Star className="h-5 w-5 mr-2" />
            Provide Feedback
          </button>
        </section>

        {/* Donation Section */}
        <section className="bg-[#272727] rounded-xl p-8">
          <h2 className="text-2xl font-bold text-white mb-4">Support Our Mission</h2>
          <p className="text-gray-300 mb-6">
            Our goal is to build community and bring as many truth seekers and game changers 
            together as possible, therefore our platform is complimentary at this point in time. We would 
            like to keep it that way as long as possible, but the reality is it's expensive to maintain 
            and run. So If our mission resonates with you, and you are able, we would kindly like to 
            implore you to consider making a donation.
          </p>
          <button
            onClick={() => window.open('https://donate.stripe.com/28EcN4cKUfNr7hQ1b73cc00', '_blank', 'noopener,noreferrer')}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
          >
            <DollarSign className="h-5 w-5 mr-2" />
            Make a Donation
          </button>
        </section>
      </div>

      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />

      <DonationModal
        isOpen={showDonationModal}
        onClose={() => setShowDonationModal(false)}
      />
    </div>
  );
};

export default About;